// src/components/admin/questions/QuestionRichEditor.tsx
// Editor rico isolado para a resolução de questões.
// Prefixo CSS: qre__  — sem dependência do RichTextEditor dos flashcards.

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
    Bold, Italic, Underline, Superscript,
    Palette, Highlighter, List, ListOrdered,
    Image as ImageIcon, RotateCcw, X,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Undo, Redo, Loader2
} from 'lucide-react';
import './QuestionRichEditor.css';

/* ─── tipos ────────────────────────────────────────────────── */
interface QuestionRichEditorProps {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}

/* ─── constantes ────────────────────────────────────────────── */
const TEXT_COLORS = [
    '#000000', '#ffffff', '#dc2626', '#2563eb',
    '#059669', '#7c3aed', '#ea580c', '#be123c',
    '#0284c7', '#16a34a', '#db2777', '#4b5563',
];
const HIGHLIGHT_COLORS = [
    '#fef08a', '#bbf7d0', '#a5f3fc', '#f9a8d4',
    '#fed7aa', '#fecdd3', '#d9f99d', '#bfdbfe',
    '#fde68a', '#fbcfe8', '#d1fae5', '#e9d5ff',
];
const SYMBOLS = [
    { char: '°', label: 'Grau' },
    { char: 'º', label: 'Ordinal masc.' },
    { char: 'ª', label: 'Ordinal fem.' },
    { char: '→', label: 'Seta direita' },
    { char: '←', label: 'Seta esquerda' },
    { char: '↑', label: 'Seta cima' },
    { char: '↓', label: 'Seta baixo' },
    { char: '≥', label: 'Maior/igual' },
    { char: '≤', label: 'Menor/igual' },
    { char: '≠', label: 'Diferente' },
    { char: '±', label: 'Mais ou menos' },
    { char: '×', label: 'Multiplicação' },
    { char: '÷', label: 'Divisão' },
    { char: '∑', label: 'Somatório' },
    { char: '√', label: 'Raiz quadrada' },
    { char: '∞', label: 'Infinito' },
    { char: 'α', label: 'Alfa' },
    { char: 'β', label: 'Beta' },
    { char: 'γ', label: 'Gama' },
    { char: 'δ', label: 'Delta' },
    { char: 'μ', label: 'Mi' },
    { char: 'Δ', label: 'Delta maiúsc.' },
];

/* ─── helpers de upload ─────────────────────────────────────── */
async function uploadImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('subdeckName', 'questoes-resolucao');
    const token = localStorage.getItem('accessToken');
    const res = await fetch('/api/upload/card-image-cloudinary', {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.message || 'Erro no upload');
    return data.url;
}

/* ════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════ */
const QuestionRichEditor: React.FC<QuestionRichEditorProps> = ({
    value,
    onChange,
    placeholder = 'Escreva a resolução...',
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const savedSelRef = useRef<{ start: number; end: number } | null>(null);
    const undoStack = useRef<string[]>([]);
    const redoStack = useRef<string[]>([]);
    const isUndoRedo = useRef(false);

    const [showColors, setShowColors] = useState(false);
    const [showHighlight, setShowHighlight] = useState(false);
    const [showSymbols, setShowSymbols] = useState(false);
    const [uploading, setUploading] = useState(false);

    /* ── Sincroniza valor externo → DOM (apenas montagem e reset externo) */
    useEffect(() => {
        if (!editorRef.current) return;
        const dom = editorRef.current.innerHTML;
        if (dom === '' && value !== '') {
            editorRef.current.innerHTML = value;
            undoStack.current = [value];
        } else if (value === '' && dom !== '') {
            editorRef.current.innerHTML = '';
            undoStack.current = [''];
            redoStack.current = [];
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── fecha dropdowns ao clicar fora */
    useEffect(() => {
        if (!showColors && !showHighlight && !showSymbols) return;
        const handler = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest('.qre__dropdown-wrap')) {
                setShowColors(false);
                setShowHighlight(false);
                setShowSymbols(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showColors, showHighlight, showSymbols]);

    /* ── handleInput */
    const handleInput = useCallback(() => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        if (!isUndoRedo.current) {
            undoStack.current.push(html);
            redoStack.current = [];
            if (undoStack.current.length > 100) undoStack.current.shift();
        }
        onChange(html);
    }, [onChange]);

    /* ── Paste limpo */
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const html = e.clipboardData.getData('text/html');
        const plain = e.clipboardData.getData('text/plain');

        let text = '';
        if (html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            ['script', 'style', 'head', 'nav', 'footer', 'aside'].forEach(
                tag => doc.querySelectorAll(tag).forEach(el => el.remove())
            );
            const extract = (node: Node): string => {
                if (node.nodeType === Node.TEXT_NODE)
                    return (node.textContent || '').replace(/\s+/g, ' ');
                if (node.nodeType !== Node.ELEMENT_NODE) return '';
                const el = node as Element;
                const tag = el.tagName.toLowerCase();
                if (['script', 'style'].includes(tag)) return '';
                const inline = ['strong', 'b', 'em', 'i', 'u', 'span', 'a', 'sup', 'sub'];
                const block = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'td', 'th', 'blockquote', 'pre'];
                if (inline.includes(tag)) return Array.from(node.childNodes).map(extract).join('');
                if (tag === 'br') return '\n';
                if (block.includes(tag)) {
                    const inner = Array.from(node.childNodes).map(extract).join('').trim();
                    return inner ? inner + '\n' : '';
                }
                return Array.from(node.childNodes).map(extract).join('');
            };
            text = Array.from(doc.body.childNodes).map(extract).join('')
                .replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
        } else {
            text = plain;
        }

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();

        // Single \n = word-wrap artifact (PDF/doc copy) → space
        // Double \n\n = actual paragraph break → <p> element
        const paragraphs = text.split(/\n\n+/);
        const frag = document.createDocumentFragment();
        paragraphs.forEach((para) => {
            const clean = para.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
            if (!clean) return;
            const p = document.createElement('p');
            p.textContent = clean;
            frag.appendChild(p);
        });
        range.insertNode(frag);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        handleInput();
    }, [handleInput]);

    /* ── Salvar / restaurar seleção */
    const saveSelection = () => {
        const sel = window.getSelection();
        if (!sel || !editorRef.current || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const pre = range.cloneRange();
        pre.selectNodeContents(editorRef.current);
        pre.setEnd(range.startContainer, range.startOffset);
        const start = pre.toString().length;
        savedSelRef.current = { start, end: start + range.toString().length };
    };

    const restoreSelection = () => {
        if (!savedSelRef.current || !editorRef.current) return;
        const { start, end } = savedSelRef.current;
        const sel = window.getSelection();
        if (!sel) return;
        let idx = 0;
        const range = document.createRange();
        range.setStart(editorRef.current, 0);
        range.collapse(true);
        const stack: Node[] = [editorRef.current];
        let foundStart = false;
        let stop = false;
        let node: Node | undefined;
        while (!stop && (node = stack.pop())) {
            if (node.nodeType === Node.TEXT_NODE) {
                const next = idx + (node.textContent?.length || 0);
                if (!foundStart && start >= idx && start <= next) {
                    range.setStart(node, start - idx); foundStart = true;
                }
                if (foundStart && end >= idx && end <= next) {
                    range.setEnd(node, end - idx); stop = true;
                }
                idx = next;
            } else {
                Array.from(node.childNodes).reverse().forEach(c => stack.push(c));
            }
        }
        sel.removeAllRanges();
        sel.addRange(range);
    };

    /* ── execCommand seguro */
    const exec = (cmd: string, val: string | null = null) => {
        if (!editorRef.current) return;
        saveSelection();
        editorRef.current.focus();
        restoreSelection();
        document.execCommand(cmd, false, val ?? undefined);
        handleInput();
    };

    /* ── Cores */
    const applyColor = (color: string) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        if (color === 'reset') document.execCommand('removeFormat');
        else document.execCommand('foreColor', false, color);
        handleInput();
        setShowColors(false);
    };
    const applyHighlight = (color: string) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand('backColor', false, color);
        handleInput();
        setShowHighlight(false);
    };

    /* ── Lista numerada — usa execCommand para comportamento nativo (Enter divide item) */
    const insertNumberedList = () => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        document.execCommand('insertOrderedList');
        handleInput();
    };

    /* ── Lista com marcadores (bullet) — idem */
    const insertBulletList = () => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        document.execCommand('insertUnorderedList');
        handleInput();
    };

    /* ── Helper compartilhado: divide LI no cursor (para listas customizadas) */
    const splitCustomListItem = useCallback((li: HTMLElement, sel: Selection) => {
        const range = sel.getRangeAt(0);
        if (!range.collapsed) range.deleteContents();

        const liText = (li.textContent || '').replace(/ /g, '').trim();

        if (!liText && !li.querySelector('img')) {
            // LI vazio: sai da lista
            const list = li.parentElement!;
            list.removeChild(li);
            const p = document.createElement('p');
            p.innerHTML = ' ';
            if (list.children.length === 0) {
                list.replaceWith(p);
            } else {
                list.parentNode?.insertBefore(p, list.nextSibling);
            }
            const nr = document.createRange();
            nr.selectNodeContents(p); nr.collapse(false);
            sel.removeAllRanges(); sel.addRange(nr);
            handleInput();
            return;
        }

        // Extrai conteúdo após o cursor para um novo LI
        const afterRange = document.createRange();
        afterRange.setStart(range.startContainer, range.startOffset);
        afterRange.setEnd(li, li.childNodes.length);
        const afterFrag = afterRange.extractContents();

        const newLi = document.createElement('li');
        const fragText = (afterFrag.textContent || '').replace(/ /g, '').trim();
        if (fragText || afterFrag.querySelector('img')) {
            newLi.appendChild(afterFrag);
        } else {
            newLi.innerHTML = ' ';
        }

        li.parentElement?.insertBefore(newLi, li.nextSibling);

        if (!(li.textContent || '').replace(/ /g, '').trim() && !li.querySelector('img')) {
            li.innerHTML = ' ';
        }

        const firstChild = newLi.firstChild;
        const nr = document.createRange();
        if (firstChild?.nodeType === Node.TEXT_NODE) {
            nr.setStart(firstChild, 0);
        } else {
            nr.setStart(newLi, 0);
        }
        nr.collapse(true);
        sel.removeAllRanges(); sel.addRange(nr);
        handleInput();
    }, [handleInput]);

    /* ── Lista alfabética */
    const insertLetterList = () => {
        if (!editorRef.current) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        // toggle: já está numa alpha list?
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).classList?.contains('qre__alpha-list')) {
                const alphaOl = node as HTMLElement;
                const frag = document.createDocumentFragment();
                Array.from(alphaOl.querySelectorAll('li')).forEach(li => {
                    const p = document.createElement('p');
                    p.innerHTML = li.innerHTML || ' ';
                    frag.appendChild(p);
                });
                alphaOl.replaceWith(frag);
                handleInput(); return;
            }
            node = (node as Node).parentNode;
        }

        const ol = document.createElement('ol');
        ol.className = 'qre__alpha-list';
        if (!range.collapsed) {
            const tmp = document.createElement('div');
            tmp.appendChild(range.cloneContents());
            tmp.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
            tmp.querySelectorAll('div,p,li').forEach(b => b.insertAdjacentText('afterend', '\n'));
            const lines = (tmp.textContent || '').split('\n').map(l => l.trim()).filter(Boolean);
            lines.forEach(l => { const li = document.createElement('li'); li.textContent = l; ol.appendChild(li); });
            range.deleteContents();
        } else {
            const li = document.createElement('li'); li.innerHTML = ' '; ol.appendChild(li);
        }
        range.insertNode(ol);
        const nr = document.createRange();
        nr.setStartAfter(ol); nr.collapse(true);
        sel.removeAllRanges(); sel.addRange(nr);
        handleInput();
    };

    /* ── Lista com algarismos romanos (I, II, III…) — negrito + recuo maior */
    const insertRomanList = () => {
        if (!editorRef.current) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        // toggle: já está numa roman list?
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).classList?.contains('qre__roman-list')) {
                const romanOl = node as HTMLElement;
                const frag = document.createDocumentFragment();
                Array.from(romanOl.querySelectorAll('li')).forEach(li => {
                    const p = document.createElement('p');
                    p.innerHTML = li.innerHTML || ' ';
                    frag.appendChild(p);
                });
                romanOl.replaceWith(frag);
                handleInput(); return;
            }
            node = (node as Node).parentNode;
        }

        const ol = document.createElement('ol');
        ol.className = 'qre__roman-list';
        if (!range.collapsed) {
            const tmp = document.createElement('div');
            tmp.appendChild(range.cloneContents());
            tmp.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
            tmp.querySelectorAll('div,p,li').forEach(b => b.insertAdjacentText('afterend', '\n'));
            const lines = (tmp.textContent || '').split('\n').map(l => l.trim()).filter(Boolean);
            lines.forEach(l => { const li = document.createElement('li'); li.textContent = l; ol.appendChild(li); });
            range.deleteContents();
        } else {
            const li = document.createElement('li'); li.innerHTML = ' '; ol.appendChild(li);
        }
        range.insertNode(ol);
        const nr = document.createRange();
        nr.setStartAfter(ol); nr.collapse(true);
        sel.removeAllRanges(); sel.addRange(nr);
        handleInput();
    };

    /* ── Símbolo */
    const insertSymbol = (char: string) => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) { editorRef.current.innerHTML += char; handleInput(); return; }
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const tn = document.createTextNode(char);
        range.insertNode(tn);
        range.setStartAfter(tn); range.collapse(true);
        sel.removeAllRanges(); sel.addRange(range);
        handleInput();
        setShowSymbols(false);
    };

    /* ── Upload de imagem */
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editorRef.current) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            editorRef.current.focus();
            const sel = window.getSelection();
            const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
            if (range) range.deleteContents();
            const img = document.createElement('img');
            img.src = url;
            img.className = 'qre__content-img';
            img.title = 'Clique para ampliar';
            if (range) {
                range.insertNode(img);
                range.setStartAfter(img); range.collapse(true);
                sel?.removeAllRanges(); sel?.addRange(range);
            } else {
                editorRef.current.appendChild(img);
            }
            handleInput();
        } catch { alert('Erro ao fazer upload da imagem'); }
        finally { setUploading(false); e.target.value = ''; }
    };

    /* ── Undo / Redo */
    const handleUndo = () => {
        if (!editorRef.current || undoStack.current.length <= 1) return;
        const cur = undoStack.current.pop()!;
        redoStack.current.push(cur);
        const prev = undoStack.current[undoStack.current.length - 1] ?? '';
        isUndoRedo.current = true;
        editorRef.current.innerHTML = prev;
        onChange(prev);
        isUndoRedo.current = false;
    };
    const handleRedo = () => {
        if (!editorRef.current || redoStack.current.length === 0) return;
        const next = redoStack.current.pop()!;
        undoStack.current.push(next);
        isUndoRedo.current = true;
        editorRef.current.innerHTML = next;
        onChange(next);
        isUndoRedo.current = false;
    };

    /* ── KeyDown: atalhos + Enter em listas customizadas */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.ctrlKey || e.metaKey) {
            switch (e.key.toLowerCase()) {
                case 'z': e.preventDefault(); handleUndo(); return;
                case 'y': e.preventDefault(); handleRedo(); return;
                case 'b': e.preventDefault(); exec('bold'); return;
                case 'i': e.preventDefault(); exec('italic'); return;
                case 'u': e.preventDefault(); exec('underline'); return;
            }
        }
        if (e.key !== 'Enter') return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        let node = sel.getRangeAt(0).commonAncestorContainer as HTMLElement;
        while (node && node !== editorRef.current) {
            if (node.tagName === 'LI') {
                const parentList = node.parentElement;
                const isCustom = parentList?.classList.contains('qre__alpha-list') ||
                                 parentList?.classList.contains('qre__roman-list');
                if (isCustom) {
                    // Listas customizadas: dividir o LI no cursor
                    e.preventDefault();
                    splitCustomListItem(node, sel);
                }
                // Listas nativas (UL/OL via execCommand): o browser trata o Enter
                return;
            }
            node = node.parentElement as HTMLElement;
        }
    };

    /* ── Botão de toolbar reutilizável */
    const Btn: React.FC<{
        onClick?: () => void;
        onMouseDown?: (e: React.MouseEvent) => void;
        title: string;
        children: React.ReactNode;
        text?: boolean;
    }> = ({ onClick, onMouseDown, title, children, text }) => (
        <button
            type="button"
            className={`qre__btn${text ? ' qre__btn--text' : ''}`}
            onClick={onClick}
            onMouseDown={onMouseDown}
            title={title}
        >
            {children}
        </button>
    );

    /* ── Divisor */
    const Sep = () => <div className="qre__sep" />;

    /* ════ RENDER ════════════════════════════════════════════ */
    return (
        <div className="qre__root">
            {/* ── TOOLBAR ─────────────────────────────────── */}
            <div className="qre__toolbar">

                {/* Formatação básica */}
                <Btn onClick={() => exec('bold')} title="Negrito (Ctrl+B)">    <Bold size={14} /></Btn>
                <Btn onClick={() => exec('italic')} title="Itálico (Ctrl+I)">    <Italic size={14} /></Btn>
                <Btn onClick={() => exec('underline')} title="Sublinhado (Ctrl+U)"> <Underline size={14} /></Btn>
                <Btn onClick={() => exec('superscript')} title="Sobrescrito">       <Superscript size={14} /></Btn>

                <Sep />

                {/* Cores */}
                <div className="qre__dropdown-wrap">
                    <Btn
                        title="Cor do texto"
                        onMouseDown={e => { e.preventDefault(); saveSelection(); setShowColors(v => !v); setShowHighlight(false); setShowSymbols(false); }}
                    >
                        <Palette size={14} />
                    </Btn>
                    {showColors && (
                        <div className="qre__dropdown qre__dropdown--colors">
                            <div className="qre__color-grid">
                                {TEXT_COLORS.map(c => (
                                    <button
                                        key={c}
                                        className="qre__color-swatch"
                                        style={{ background: c }}
                                        onMouseDown={e => { e.preventDefault(); applyColor(c); }}
                                        title={c}
                                    />
                                ))}
                            </div>
                            <button className="qre__color-reset" onMouseDown={e => { e.preventDefault(); applyColor('reset'); }}>
                                <RotateCcw size={12} /> Resetar cor
                            </button>
                        </div>
                    )}
                </div>

                <div className="qre__dropdown-wrap">
                    <Btn
                        title="Marcador de texto"
                        onMouseDown={e => { e.preventDefault(); saveSelection(); setShowHighlight(v => !v); setShowColors(false); setShowSymbols(false); }}
                    >
                        <Highlighter size={14} />
                    </Btn>
                    {showHighlight && (
                        <div className="qre__dropdown qre__dropdown--colors">
                            <div className="qre__color-grid">
                                {HIGHLIGHT_COLORS.map(c => (
                                    <button
                                        key={c}
                                        className="qre__color-swatch"
                                        style={{ background: c }}
                                        onMouseDown={e => { e.preventDefault(); applyHighlight(c); }}
                                        title={c}
                                    />
                                ))}
                            </div>
                            <button className="qre__color-reset" onMouseDown={e => { e.preventDefault(); applyHighlight('transparent'); setShowHighlight(false); }}>
                                <X size={12} /> Remover marcação
                            </button>
                        </div>
                    )}
                </div>

                <Sep />

                {/* Undo / Redo */}
                <Btn onClick={handleUndo} title="Desfazer (Ctrl+Z)"><Undo size={14} /></Btn>
                <Btn onClick={handleRedo} title="Refazer (Ctrl+Y)"> <Redo size={14} /></Btn>

                <Sep />

                {/* Listas */}
                <Btn onClick={insertNumberedList} title="Lista numerada">    <ListOrdered size={14} /></Btn>
                <Btn onClick={insertBulletList} title="Lista com marcadores"><List size={14} /></Btn>
                <Btn onClick={insertLetterList} title="Lista alfabética" text>a)</Btn>
                <Btn onClick={insertRomanList} title="Lista em algarismos romanos" text>I)</Btn>

                <Sep />

                {/* Alinhamento */}
                <Btn onClick={() => exec('justifyLeft')} title="Alinhar à esquerda"><AlignLeft size={14} /></Btn>
                <Btn onClick={() => exec('justifyCenter')} title="Centralizar">        <AlignCenter size={14} /></Btn>
                <Btn onClick={() => exec('justifyRight')} title="Alinhar à direita">  <AlignRight size={14} /></Btn>
                <Btn onClick={() => exec('justifyFull')} title="Justificar">         <AlignJustify size={14} /></Btn>

                <Sep />

                {/* Símbolos */}
                <div className="qre__dropdown-wrap">
                    <Btn
                        title="Inserir símbolo"
                        text
                        onMouseDown={e => { e.preventDefault(); saveSelection(); setShowSymbols(v => !v); setShowColors(false); setShowHighlight(false); }}
                    >
                        Ω
                    </Btn>
                    {showSymbols && (
                        <div className="qre__dropdown qre__dropdown--symbols">
                            {SYMBOLS.map(s => (
                                <button
                                    key={s.char}
                                    className="qre__symbol-btn"
                                    onMouseDown={e => { e.preventDefault(); insertSymbol(s.char); }}
                                    title={s.label}
                                >
                                    {s.char}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <Sep />

                {/* Imagem */}
                <label className={`qre__btn qre__btn--upload ${uploading ? 'qre__btn--loading' : ''}`} title="Inserir imagem">
                    {uploading ? <Loader2 size={14} className="qre__spin" /> : <ImageIcon size={14} />}
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
                </label>

            </div>

            {/* ── ÁREA DE CONTEÚDO ────────────────────────── */}
            <div
                ref={editorRef}
                contentEditable
                className="qre__content"
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                data-placeholder={placeholder}
                suppressContentEditableWarning
            />
        </div>
    );
};

export default QuestionRichEditor;